import { useState } from "react";
import { CircleDot } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTheme } from "@/lib/theme/ThemeProvider";
import ConfirmAction from "@/components/appearance/ConfirmAction";

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return { title: d };
  return { title: d?.message || "Couldn't save your appearance", description: d?.issues?.map((i) => i.message).join(" ") };
};

export function useSaveAppearance() {
  const th = useTheme();
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await th.savePersonal();
      toast.success("Appearance saved for your account");
    } catch (e) {
      const m = errText(e);
      toast.error(m.title, { description: m.description });
    } finally {
      setBusy(false);
    }
  };
  const blocked = th.personalDraft?.mode === "custom" && (th.preview?.issues || []).length > 0;
  return { save, busy, blocked };
}

export default function AppearanceActions({ stacked }) {
  const th = useTheme();
  const { save, busy, blocked } = useSaveAppearance();
  const [confirm, setConfirm] = useState(false);
  const eff = th.eff;
  const clinicDraft = th.draft?.kind === "clinic";
  const hasPersonal = eff?.saved && (eff.mode !== "clinic" || eff.density !== "comfortable" || eff.text_scale !== "standard");

  async function reset() {
    setConfirm(false);
    try {
      await th.resetPersonal();
      toast.success("Back to the clinic default");
    } catch (e) {
      toast.error(errText(e).title);
    }
  }

  return (
    <div className={cn("border-t border-le-border bg-le-surface px-5 py-3", stacked ? "space-y-2" : "flex flex-wrap items-center gap-2")} data-testid="appearance-actions">
      {th.dirty && !clinicDraft && (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-800" data-testid="appearance-unsaved">
          <CircleDot className="h-3.5 w-3.5" aria-hidden /> Unsaved changes
        </span>
      )}
      <div className={stacked ? "grid grid-cols-2 gap-2" : "ml-auto flex flex-wrap gap-2"}>
        <Button onClick={save} disabled={!th.dirty || blocked || busy || clinicDraft} className={stacked ? "col-span-2 order-first" : "order-last"} data-testid="appearance-save-btn">
          {busy ? "Saving…" : "Save for my account"}
        </Button>
        <Button variant="ghost" onClick={() => (hasPersonal ? setConfirm(true) : reset())} disabled={clinicDraft} data-testid="appearance-reset-btn">
          Reset to clinic default
        </Button>
        <Button variant="outline" onClick={th.cancelPreview} disabled={!th.draft} data-testid="appearance-cancel-btn">
          Cancel
        </Button>
      </div>
      <ConfirmAction
        open={confirm}
        onOpenChange={setConfirm}
        testId="appearance-reset-confirm"
        title="Reset to the clinic default?"
        description="This replaces your saved personal theme, density and text size with the clinic default. Nothing else changes."
        confirmLabel="Reset my appearance"
        onConfirm={reset}
      />
    </div>
  );
}
